import { useEffect, useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { useAuth } from './useAuth';
import { useNotifications } from '../contexts/NotificationsContext';
import type { RealtimeChannel } from '@supabase/supabase-js';

// Module-level singleton state to prevent multiple setups across component remounts
let globalMessageChannel: RealtimeChannel | null = null;
let globalChatChannel: RealtimeChannel | null = null;
let globalCurrentUserId: string | null = null;
let globalMessageReconnectTimeout: NodeJS.Timeout | null = null;
let globalChatReconnectTimeout: NodeJS.Timeout | null = null;
let globalIsReconnecting = { message: false, chat: false };

/**
 * Centralized real-time manager for all chat-related subscriptions
 * This prevents conflicts between individual chat subscriptions and chat list subscriptions
 * Uses module-level state to prevent multiple setups during React remounts
 */
export function useRealtimeManager() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { addNotification } = useNotifications();
  
  // Store user in ref to avoid recreating callbacks when user object changes
  const userRef = useRef(user);
  userRef.current = user;
  
  // Store queryClient and addNotification in refs for stable callbacks
  const queryClientRef = useRef(queryClient);
  queryClientRef.current = queryClient;
  const addNotificationRef = useRef(addNotification);
  addNotificationRef.current = addNotification;

  // Setup message subscription with reconnection logic - uses refs for stability
  const setupMessageSubscription = useCallback(() => {
    const currentUser = userRef.current;
    if (!currentUser) return;

    // Clean up existing message channel first
    if (globalMessageChannel) {
      supabase.removeChannel(globalMessageChannel);
      globalMessageChannel = null;
    }

    console.log('🟢 Setting up message subscription for user:', currentUser.id);

    // Single subscription for all message events
    const messageSubscription = supabase
      .channel(`realtime-messages-${currentUser.id}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'messages',
      }, async (payload) => {
        const userId = userRef.current?.id;
        console.log('🟢 Centralized manager received new message:', {
          messageId: payload.new.id,
          chatId: payload.new.chat_id,
          senderId: payload.new.sender_id,
          isFromCurrentUser: payload.new.sender_id === userId
        });

        try {
          // Get sender profile for the new message
          const { data: senderProfile } = await supabase
            .from('profiles')
            .select('id, full_name, username, avatar_url')
            .eq('id', payload.new.sender_id)
            .single();

          if (!senderProfile) {
            console.error('🟢 Could not find sender profile for:', payload.new.sender_id);
            return;
          }

          const newMessage = {
            ...payload.new,
            sender: senderProfile
          };

          // Update chat list with new last message (always)
          queryClientRef.current.setQueryData(['chats', userId], (old: any[] = []) => {
            if (!Array.isArray(old)) return old;
            
            return old.map(chat => {
              if (chat.id === payload.new.chat_id) {
                return {
                  ...chat,
                  last_message: {
                    id: newMessage.id,
                    chat_id: payload.new.chat_id,
                    text: newMessage.text,
                    created_at: newMessage.created_at,
                    sender_id: newMessage.sender_id,
                    sender: senderProfile
                  },
                  updated_at: newMessage.created_at
                };
              }
              return chat;
            });
          });

          // Only create notifications for messages from others
          if (payload.new.sender_id !== userId) {
            const senderName = senderProfile.username 
              ? `@${senderProfile.username}` 
              : senderProfile.full_name || 'User';
            
            addNotificationRef.current({
              id: `msg-${Date.now()}`,
              title: `New message from ${senderName}`,
              description: payload.new.text.length > 30 
                ? `${payload.new.text.substring(0, 30)}...` 
                : payload.new.text,
              time: 'Just now',
              type: 'message',
              data: { chatId: payload.new.chat_id }
            });
          }

          console.log('🟢 Successfully processed new message in centralized manager');
        } catch (error) {
          console.error('🟢 Error processing new message in centralized manager:', error);
          
          // Fallback: invalidate relevant queries
          queryClientRef.current.invalidateQueries({ queryKey: ['chats', userId] });
          queryClientRef.current.invalidateQueries({ queryKey: ['chat-messages', payload.new.chat_id] });
        }
      })
      .subscribe((status) => {
        const statusStr = typeof status === 'string' ? status : String(status || 'UNKNOWN');
        console.log('🟢 Centralized message subscription status:', statusStr);
        
        if (statusStr === 'SUBSCRIBED') {
          console.log('🟢 ✅ Successfully subscribed to centralized messages');
          // Clear any pending reconnection attempts
          if (globalMessageReconnectTimeout) {
            clearTimeout(globalMessageReconnectTimeout);
            globalMessageReconnectTimeout = null;
          }
          globalIsReconnecting.message = false;
        } else if (statusStr === 'CHANNEL_ERROR' || statusStr === 'TIMED_OUT') {
          console.error('🟢 ❌ Channel error for centralized message subscription:', statusStr);
        } else if (statusStr === 'CLOSED') {
          // CLOSED is expected during cleanup/reconnection, don't log as error
          console.log('🟢 Centralized message subscription closed (expected during cleanup)');
          
          // Prevent multiple simultaneous reconnection attempts
          if (globalIsReconnecting.message) {
            console.log('🟢 Message reconnection already in progress, skipping...');
            return;
          }
          
          // Clear any existing timeout
          if (globalMessageReconnectTimeout) {
            clearTimeout(globalMessageReconnectTimeout);
          }
          
          globalIsReconnecting.message = true;
          globalMessageReconnectTimeout = setTimeout(() => {
            console.log('🟢 🔄 Attempting to reconnect message subscription...');
            globalIsReconnecting.message = false;
            setupMessageSubscription();
          }, 3000); // Retry after 3 seconds
        }
      });

    globalMessageChannel = messageSubscription;
  }, []); // No dependencies - uses refs for all external values

  // Setup chat subscription with reconnection logic - uses refs for stability
  const setupChatSubscription = useCallback(() => {
    const currentUser = userRef.current;
    if (!currentUser) return;

    // Clean up existing chat channel first
    if (globalChatChannel) {
      supabase.removeChannel(globalChatChannel);
      globalChatChannel = null;
    }

    console.log('🟢 Setting up chat subscription for user:', currentUser.id);

    // Subscription for chat updates (creation, deletion, etc.)
    // Filter to only listen to chats where the current user is a participant
    const chatSubscription = supabase
      .channel(`realtime-chats-${currentUser.id}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'chats',
      }, (payload) => {
        // Determine event type from payload contents (new = INSERT/UPDATE, old = UPDATE/DELETE)
        const eventType = payload.new && payload.old ? 'UPDATE' : payload.new ? 'INSERT' : 'DELETE';
        const userId = userRef.current?.id;
        console.log('🟢 Chat update detected:', {
          event: eventType,
          chatId: payload.new?.id || payload.old?.id,
        });
        queryClientRef.current.invalidateQueries({ queryKey: ['chats', userId] });
      })
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'chat_participants',
        filter: `user_id=eq.${currentUser.id}`, // Only listen to changes for current user's participants
      }, (payload) => {
        // Determine event type from payload contents (new = INSERT/UPDATE, old = UPDATE/DELETE)
        const eventType = payload.new && payload.old ? 'UPDATE' : payload.new ? 'INSERT' : 'DELETE';
        const userId = userRef.current?.id;
        console.log('🟢 Chat participants update detected for current user:', {
          event: eventType,
          chatId: payload.new?.chat_id || payload.old?.chat_id,
          userId: payload.new?.user_id || payload.old?.user_id
        });
        queryClientRef.current.invalidateQueries({ queryKey: ['chats', userId] });
      })
      .subscribe((status) => {
        const statusStr = typeof status === 'string' ? status : String(status || 'UNKNOWN');
        console.log('🟢 Centralized chat subscription status:', statusStr);
        
        if (statusStr === 'SUBSCRIBED') {
          console.log('🟢 ✅ Successfully subscribed to centralized chats');
          // Clear any pending reconnection attempts
          if (globalChatReconnectTimeout) {
            clearTimeout(globalChatReconnectTimeout);
            globalChatReconnectTimeout = null;
          }
          globalIsReconnecting.chat = false;
        } else if (statusStr === 'CHANNEL_ERROR' || statusStr === 'TIMED_OUT') {
          console.error('🟢 ❌ Chat subscription error:', statusStr);
        } else if (statusStr === 'CLOSED') {
          // CLOSED is expected during cleanup/reconnection, don't log as error
          console.log('🟢 Chat subscription closed (expected during cleanup)');
          
          // Prevent multiple simultaneous reconnection attempts
          if (globalIsReconnecting.chat) {
            console.log('🟢 Chat reconnection already in progress, skipping...');
            return;
          }
          
          // Clear any existing timeout
          if (globalChatReconnectTimeout) {
            clearTimeout(globalChatReconnectTimeout);
          }
          
          globalIsReconnecting.chat = true;
          globalChatReconnectTimeout = setTimeout(() => {
            console.log('🟢 🔄 Attempting to reconnect chat subscription...');
            globalIsReconnecting.chat = false;
            setupChatSubscription();
          }, 3000); // Retry after 3 seconds
        }
      });

    globalChatChannel = chatSubscription;
  }, []); // No dependencies - uses refs for all external values

  useEffect(() => {
    const userId = user?.id ?? null;
    
    // Only restart if user ID actually changed - use module-level state
    if (userId === globalCurrentUserId) {
      return;
    }
    
    // Clean up old subscriptions before setting up new ones
    if (globalMessageChannel) {
      supabase.removeChannel(globalMessageChannel);
      globalMessageChannel = null;
    }
    if (globalChatChannel) {
      supabase.removeChannel(globalChatChannel);
      globalChatChannel = null;
    }
    if (globalMessageReconnectTimeout) {
      clearTimeout(globalMessageReconnectTimeout);
      globalMessageReconnectTimeout = null;
    }
    if (globalChatReconnectTimeout) {
      clearTimeout(globalChatReconnectTimeout);
      globalChatReconnectTimeout = null;
    }
    globalIsReconnecting = { message: false, chat: false };
    
    globalCurrentUserId = userId;
    
    if (!userId) {
      return;
    }

    console.log('🟢 Starting centralized real-time manager for user:', userId);
    
    // Setup both subscriptions
    setupMessageSubscription();
    setupChatSubscription();

    // NOTE: No cleanup function - we use module-level state that persists across remounts
    // Cleanup only happens when user ID actually changes (handled at start of effect)
  }, [user?.id, setupMessageSubscription, setupChatSubscription]); // Only depend on user ID - callbacks are stable

  // Connection health monitoring - uses global state
  useEffect(() => {
    const userId = user?.id;
    if (!userId) return;

    const healthCheck = setInterval(() => {
      // Skip health check if user changed
      if (globalCurrentUserId !== userId) return;
      
      const channels = supabase.getChannels();
      const messageChannel = globalMessageChannel;
      const chatChannel = globalChatChannel;
      
      const messageChannelState = messageChannel ? messageChannel.state : 'NOT_FOUND';
      const chatChannelState = chatChannel ? chatChannel.state : 'NOT_FOUND';
      
      console.log('🟢 Real-time health check:', {
        totalChannels: channels.length,
        messageChannelState,
        chatChannelState,
        messageChannelTopic: messageChannel?.topic,
        chatChannelTopic: chatChannel?.topic
      });

      // If channels are in error state, attempt to reconnect
      if (messageChannelState === 'CHANNEL_ERROR' || messageChannelState === 'TIMED_OUT' || messageChannelState === 'CLOSED') {
        console.warn('🟢 ⚠️ Message channel in error state, attempting reconnection...');
        setupMessageSubscription();
      }
      
      if (chatChannelState === 'CHANNEL_ERROR' || chatChannelState === 'TIMED_OUT' || chatChannelState === 'CLOSED') {
        console.warn('🟢 ⚠️ Chat channel in error state, attempting reconnection...');
        setupChatSubscription();
      }
    }, 30000); // Check every 30 seconds

    return () => clearInterval(healthCheck);
  }, [user?.id, setupMessageSubscription, setupChatSubscription]); // Callbacks are stable
}